import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input, Spinner, Chip, Tooltip, Avatar, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button } from '@heroui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '../../../../../contexts/translation.context';
import { Typography, UniversalTable } from '../../../../../components';
import IdView from '../../../../../components/id-view';
import { CalendarIcon } from '../../../../../components/icons/ComplexIcons';
import { DollarRoundedIc, EmptyContentIc, GroupUserIc, InfoCircle } from '../../../../../components/icons/BasicIcons';
import { FilterSelect } from '../../../../../components/ui';
import {
  OnlyFansIc,
  InstagramIc,
  GoogleIc,
  TwitterIc,
  IcReddit,
  IcAnotherSoc,
  IcTg,
  IcTikTok,
  IcYoutube,
  SearchInputIc,
  ResetFiltersIc,
} from '../../../../../components/icons';
import { staffApi, GetPartnerCPLOffersParams, CPLOffer } from '../../../../../api/staff';
import { formatTimeAgo } from '../../../../../utils/formatTimeAgo';

interface PartnerCPLOffersTabProps {
  partnerId: string;
}

/**
 * PartnerCPLOffersTab Component
 *
 * Displays CPL offers for a specific partner with filtering and pagination.
 * Based on MyOffersTable from onlyu_front.
 */
const PartnerCPLOffersTab: React.FC<PartnerCPLOffersTabProps> = ({ partnerId }) => {
  const { t } = useTranslations();
  const navigate = useNavigate();

  // State
  const [offers, setOffers] = useState<CPLOffer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [localSearch, setLocalSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState<{
    status?: 'active' | 'hidden' | 'moderation' | 'deactivated' | 'all';
  }>({
    status: undefined,
  });

  // Status options
  const statusOptions = useMemo(() => [
    { key: 'all', label: t('salesFans.filters.status.all') || 'Все статусы' },
    { key: 'active', label: t('salesFans.tableData.status.active') || 'Активный' },
    { key: 'hidden', label: t('salesFans.tableData.status.hidden') || 'Скрыт' },
    { key: 'moderation', label: t('salesFans.tableData.status.moderation') || 'Модерация' },
    { key: 'deactivated', label: t('salesFans.tableData.status.deactivated') || 'Деактивирован' },
  ], [t]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchValue !== '' ||
           (filters.status !== undefined && filters.status !== 'all');
  }, [searchValue, filters]);

  // Define table columns
  const columns = useMemo(() => [
    { key: "id", name: "ID", sortable: false },
    { key: "subscriptionType", name: t('salesFans.tableColumns.subscriptionType') || 'Тип подписки', sortable: false },
    { key: "offer", name: t('salesFans.tableColumns.offer') || 'Оффер', sortable: false },
    { key: "description", name: t('salesFans.tableColumns.description') || 'Описание', sortable: false },
    { key: "status", name: t('salesFans.tableColumns.status') || 'Статус', sortable: false },
    { key: "createdAt", name: t('salesFans.tableColumns.createdAt') || 'Дата создания', sortable: false },
    { key: "actions", name: "", sortable: false }
  ], [t]);

  // Fetch CPL offers for partner
  const fetchOffers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: Omit<GetPartnerCPLOffersParams, 'partner_id'> = {
        page: currentPage,
        page_size: pageSize,
        ...(searchValue && { search: searchValue }),
        ...(filters.status && filters.status !== 'all' && {
          status: filters.status as 'active' | 'hidden' | 'moderation' | 'deactivated'
        }),
      };

      const response = await staffApi.getPartnerCPLOffers(partnerId, params);
      setOffers(response?.objects || []);
      setTotalCount(response?.count || 0);
    } catch (error) {
      console.error('Failed to load partner CPL offers:', error);
      setOffers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [partnerId, currentPage, pageSize, searchValue, filters.status]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(localSearch);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Handlers
  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    if (key === 'status') {
      setFilters(prev => ({
        ...prev,
        status: value === 'all' ? undefined : value as 'active' | 'hidden' | 'moderation' | 'deactivated'
      }));
    }
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setLocalSearch('');
    setSearchValue('');
    setFilters({
      status: undefined,
    });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Get status dot color
  const getStatusDotColor = (status: CPLOffer['status']) => {
    switch (status) {
      case 'active': return 'bg-[#17C964]';
      case 'moderation': return 'bg-[#7828C8]';
      case 'hidden': return 'bg-[#F5A524]';
      case 'deactivated': return 'bg-[#71717A]';
      default: return 'bg-[#71717A]';
    }
  };

  // Get partner level style
  const getPartnerLevelStyle = (lvl: string) => {
    switch (lvl) {
      case 'expert': return { bg: 'bg-[#7828c8]/20', text: 'text-[#7828c8]', label: 'expert' };
      case 'pro': return { bg: 'bg-[#11181c]', text: 'text-white', label: 'pro' };
      case 'new': return { bg: 'bg-[#17c964]/20', text: 'text-[#17C964]', label: 'new' };
      default: return { bg: 'bg-[#EEEEEF]', text: 'text-[#71717A]', label: 'base' };
    }
  };

  // Get source icon
  const getSourceIcon = (source: string) => {
    const iconSize = 14;
    switch (source) {
      case 'only_fans':
        return <OnlyFansIc size={iconSize} fill='#A8A8B0' />;
      case 'reddit':
        return <IcReddit size={iconSize} />;
      case 'instagram':
        return <InstagramIc size={iconSize} fill='#A8A8B0' />;
      case 'tiktok':
        return <IcTikTok size={iconSize} />;
      case 'google':
        return <GoogleIc size={iconSize} fill='#A8A8B0' />;
      case 'telegram':
        return <IcTg size={iconSize} />;
      case 'twitter':
        return <TwitterIc size={iconSize} fill='#A8A8B0' />;
      case 'youtube':
        return <IcYoutube size={iconSize} />;
      case 'another':
        return <IcAnotherSoc size={iconSize} />;
      default:
        return <IcAnotherSoc size={iconSize} />;
    }
  };

  // Get source label
  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'only_fans': return 'OnlyFans';
      case 'reddit': return 'Reddit';
      case 'instagram': return 'Instagram';
      case 'tiktok': return 'TikTok';
      case 'google': return 'Google';
      case 'telegram': return 'Telegram';
      case 'twitter': return 'Twitter';
      case 'youtube': return 'YouTube';
      case 'another': return t('salesFans.labels.otherSource') || 'Другие';
      default: return source;
    }
  };

  // Cell renderer
  const renderCell = useCallback((offer: CPLOffer, columnKey: string) => {
    switch (columnKey) {
      case "id":
        return (
          <IdView textToCopy={offer.id}>
            {offer.id.slice(-4)}
          </IdView>
        );

      case "subscriptionType":
        // Subscription Type Cell: Display subscription type badge with pricing info
        const subType = offer.subscription_type || 'free';
        const isFree = subType === 'free';

        return (
          <div className="flex flex-col gap-[8px]">
            {/* Subscription Type Badge */}
            <div className="flex gap-[4px] items-center">
              <Typography variant='label3' className={`${isFree ? '!uppercase' : '!capitalize'} font-semibold`}>
                {isFree ? 'FREE' : subType}
              </Typography>
            </div>

            {/* Pricing Information */}
            <div className="flex flex-col gap-[4px]">
              {offer.price !== undefined && (
                <Typography variant="body3" className="text-[#11181C]">
                  <span className="font-semibold">${offer.price.toFixed(2)}</span>
                  <span className="text-[#71717A]"> / {t('salesFans.tableData.perFan') || 'фан'}</span>
                </Typography>
              )}
              {offer.fans_min !== undefined && (
                <Typography variant="body3" className="text-[#71717A]">
                  {offer.fans_min}+ {t('salesFans.tableData.fans') || 'фанов'}
                  {offer.fans_per_day !== undefined && (
                    <span> / ~{offer.fans_per_day} {t('salesFans.tableData.perDay') || 'в день'}</span>
                  )}
                </Typography>
              )}
            </div>
          </div>
        );

      case "offer":
        // Get price from subs array if available, otherwise use price field
        const subsPrice = offer.subs && offer.subs.length > 0 ? parseFloat(offer.subs[0].price) : offer.price;
        const hasSubsRange = offer.subs_min !== undefined && offer.subs_max !== undefined;

        return (
          <div className="flex flex-col gap-[4px] items-start">
            {/* Source Badge */}
            <div className="flex items-center gap-[4px] px-[8px] py-[4px] border !h-[24px] border-[1.4px] border-[#d4d4d8] rounded-xl">
              {getSourceIcon(offer.source)}
              <Typography variant='body3' className="text-[12px]">
                {getSourceLabel(offer.source)}
              </Typography>
              {offer.description && (
                <>
                  <div className="w-[1px] h-[12px] bg-[#d4d4d8] mx-[4px]"></div>
                  <Tooltip
                    content={offer.description}
                    placement="top"
                  >
                    <div className="cursor-pointer flex items-center">
                      <InfoCircle/>
                    </div>
                  </Tooltip>
                </>
              )}
            </div>

            {/* Price and Subscribers Range */}
            <div className="flex flex-col gap-[4px]">
              {subsPrice !== undefined && (
                <div className="flex flex-wrap items-center gap-x-[8px] gap-y-[4px]">
                  <div className="flex items-center gap-[4px] flex-shrink-0 whitespace-nowrap">
                    <div className='min-w-[12px] max-w-[12px] flex-shrink-0'>
                      <DollarRoundedIc/>
                    </div>
                    <span className="text-[14px] font-semibold text-gray-900 whitespace-nowrap">
                      ${subsPrice.toFixed(2)}
                    </span>
                  </div>
                  <Typography variant='body3' className="text-[#71717A] whitespace-nowrap">
                    {t('salesFans.tableData.perFan') || '/фан'}
                  </Typography>
                </div>
              )}
              {hasSubsRange && (
                <div className="flex flex-wrap items-center gap-x-[8px] gap-y-[4px]">
                  <div className="flex items-center gap-[4px] flex-shrink-0 whitespace-nowrap">
                    <div className='min-w-[12px] max-w-[12px] flex-shrink-0'>
                      <GroupUserIc/>
                    </div>
                    <span className="text-[14px] font-semibold text-gray-900 whitespace-nowrap">
                      {offer.subs_min}-{offer.subs_max}
                    </span>
                  </div>
                  <Typography variant='body3' className="text-[#71717A] whitespace-nowrap">
                    {t('salesFans.tableData.subscribers') || 'подписчиков'}
                    {/* @ts-ignore */}
                    {offer?.subs_pre_day !== undefined && (
                      //@ts-ignore
                      <span> / ~{offer?.subs_pre_day} {t('salesFans.tableData.perDay') || 'в день'}</span>
                    )}
                  </Typography>
                </div>
              )}
            </div>
          </div>
        );

      case "description":
        // Orders stats - placeholder with zeros
        return (
          <div className="flex flex-col gap-[4px]">
            <div className="flex items-center gap-[4px]">
              <div className="w-[7px] h-[7px] bg-[#17C964] rounded-full"></div>
              <Typography variant='body3' className="text-[#71717A]">
                {t('salesFans.tableData.active')}: <Typography variant='label3'>0</Typography>
              </Typography>
            </div>
            <div className="flex items-center gap-[4px]">
              <div className="w-[7px] h-[7px] bg-[#006FEE] rounded-full"></div>
              <Typography variant='body3' className="text-[#71717A]">
                {t('salesFans.tableData.completed')}: <Typography variant='label3'>0</Typography>
              </Typography>
            </div>
            <div className="flex items-center gap-[4px]">
              <div className="w-[7px] h-[7px] bg-[#A1A1AA] rounded-full"></div>
              <Typography variant='body3' className="text-[#71717A]">
                {t('salesFans.tableData.cancelled')}: <Typography variant='label3'>0</Typography>
              </Typography>
            </div>
          </div>
        );

      case "status":
        const statusText = (() => {
          if (offer.status === 'active') return t('salesFans.tableData.status.active') || 'Активен';
          if (offer.status === 'moderation') return t('salesFans.tableData.status.moderation') || 'Модерация';
          if (offer.status === 'hidden') return t('salesFans.tableData.status.hidden') || 'Скрыт';
          if (offer.status === 'deactivated') return t('salesFans.tableData.status.deactivated') || 'Деактивирован';
          return offer.status;
        })();

        return (
          <Chip
            className="bg-[#F4F4F5] flex items-center px-[8px] gap-[4px] rounded-[8px] h-[24px] whitespace-nowrap"
            startContent={
              <div className={`w-[7px] h-[7px] rounded-full ${getStatusDotColor(offer.status)}`}></div>
            }
          >
            <Typography variant='body3' className="whitespace-nowrap">{statusText}</Typography>
          </Chip>
        );

      case "createdAt":
        // Created At Cell: Shows time ago with tooltip
        return (
          <div className="flex items-center gap-[4px]">
            <CalendarIcon size={14} className="text-gray-600" />
            <Tooltip
              content={
                <div className='flex flex-col'>
                  <Typography variant='body3' className="text-[#71717A]">
                    {t('salesFans.tableData.createdAt') || 'Создан'}:
                  </Typography>
                  <Typography variant='body2'>
                    {formatTimeAgo(offer.created_at, t)}
                  </Typography>
                </div>
              }
              placement="top"
            >
              <Typography variant="body3" className="text-[#71717A]">
                {formatTimeAgo(offer.created_at, t)}
              </Typography>
            </Tooltip>
          </div>
        );

      case "actions":
        return (
          <Dropdown>
            <DropdownTrigger>
              <Button
                size="sm"
                variant="light"
                className="min-w-[32px] w-[32px] h-[32px] p-0"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 5.83333C10.9205 5.83333 11.6667 5.08714 11.6667 4.16667C11.6667 3.24619 10.9205 2.5 10 2.5C9.07953 2.5 8.33333 3.24619 8.33333 4.16667C8.33333 5.08714 9.07953 5.83333 10 5.83333Z" fill="#71717A"/>
                  <path d="M10 11.6667C10.9205 11.6667 11.6667 10.9205 11.6667 10C11.6667 9.07953 10.9205 8.33333 10 8.33333C9.07953 8.33333 8.33333 9.07953 8.33333 10C8.33333 10.9205 9.07953 11.6667 10 11.6667Z" fill="#71717A"/>
                  <path d="M10 17.5C10.9205 17.5 11.6667 16.7538 11.6667 15.8333C11.6667 14.9129 10.9205 14.1667 10 14.1667C9.07953 14.1667 8.33333 14.9129 8.33333 15.8333C8.33333 16.7538 9.07953 17.5 10 17.5Z" fill="#71717A"/>
                </svg>
              </Button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Offer actions">
              {offer.status === 'active' && (
                <DropdownItem key="deactivate" className="text-danger" color="danger">
                  {t('salesFans.deactivate') || 'Деактивировать'}
                </DropdownItem>
              )}
              {offer.status === 'deactivated' && (
                <DropdownItem key="activate" className="text-success" color="success">
                  {t('salesFans.activate') || 'Активировать'}
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        );

      default:
        return null;
    }
  }, [t, navigate]);

  // Empty content component
  const emptyContent = (
    <div className="text-center py-[48px]">
      <div className="flex flex-col items-center gap-[16px]">
        <div className="flex items-center flex-col gap-[6px]">
          <EmptyContentIc/>
          <Typography variant="h6" className="text-[20px] font-semibold leading-[28px]">
            {hasActiveFilters
              ? (t('salesFans.noOffersFiltered') || 'По выбранным фильтрам нет офферов')
              : (t('salesFans.noOffers') || 'Нет офферов')
            }
          </Typography>
          <Typography className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]" variant="body1">
            {hasActiveFilters
              ? (t('salesFans.noOffersFilteredDescription') || 'Попробуйте изменить параметры фильтров')
              : (t('salesFans.noOffersDescription') || 'У этого партнера пока нет CPL офферов')
            }
          </Typography>
        </div>
      </div>
    </div>
  );

  // Loading state
  if (isLoading && offers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Filters Row */}
      <div className="flex items-center gap-[16px] flex-wrap mb-[16px]">
        {/* Search Input */}
        <Input
          type="text"
          placeholder={t('salesFans.searchPlaceholder') || 'Поиск по ID'}
          size="sm"
          value={localSearch}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: "w-[273px]",
            base: "w-[273px]",
            input: "text-sm",
            inputWrapper: "rounded-lg h-[36px] w-[273px]",
          }}
        />

        {/* Status Filter */}
        <FilterSelect
          selectedKey={filters.status || 'all'}
          onSelectionChange={(value) => handleFilterChange('status', value)}
          options={statusOptions}
          placeholder={t('salesFans.status') || 'Статус'}
          variant="campaign"
          autoWidth={true}
        />

        {/* Reset Filters Button */}
        {hasActiveFilters && (
          <Tooltip content={t('filters.reset') || 'Сбросить фильтры'} placement="top">
            <button
              className="h-[36px] w-[36px] rounded-[8px] bg-[#F4F4F5] flex items-center justify-center hover:bg-[#E4E4E7] transition-colors"
              onClick={handleResetFilters}
            >
              <ResetFiltersIc/>
            </button>
          </Tooltip>
        )}
      </div>

      {/* Table */}
      <div className="flex-1">
        <UniversalTable
          data={offers}
          columns={columns}
          renderCell={renderCell}
          emptyContent={emptyContent}
          pagination={{
            count: totalCount,
            page_size: pageSize,
            page: currentPage,
          }}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          currentPage={currentPage}
          currentPageSize={pageSize}
        />
      </div>
    </div>
  );
};

export default PartnerCPLOffersTab;
